import { useToast } from '@repo/design-system/index'
import { QIcon_search_default } from '@repo/icons/index'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { useContext, useEffect, useState } from 'react'
import { useEventTracking } from '../../../../helpers/gtmHelper'
import apis from '../../../../modules/apis'
import { axiosGet } from '../../../../modules/axiosHelper'
import { APP_SCREEN_NAME, STACK_ACTIVITY_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import { useOnclickHeartPacket } from '../../../../modules/hooks/common/useOnclickHeartPacket'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_material, n_material_list } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import {
  PREV_TWO_HOUR_FROM_LESSON,
  SelectMaterialContext,
} from '../../../../pages/[lang]/portal/lessons/materials/[lessonId]'
import { AI_TUTOR_ACTIVITIES_MAP, useAITutorStack } from '../../../../pages/[lang]/webview/ai-tutor'
import { useConversationAIStack } from '../../../../pages/[lang]/webview/conversational-ai'
import { LessonsActivityNamesType, useLessonsStack } from '../../../../pages/[lang]/webview/lessons'
import { useMaterialsStack } from '../../../../pages/[lang]/webview/materials'
import CategoryListSection from './CategoryListSection'
import CategorySection from './CategorySection'
import PrevHourMaterialSelect from './PrevHourMaterialSelect'
import RecommendedListSection from './RecommendedListSection'
import SelectMaterialCard, { SelectMaterialCardSkeleton } from './SelectMaterialCard'

export const BOOKMARKED_MATERIALS = 99999

interface ISearchResultListSection {
  keywords: string
  isList?: boolean
  isConversation?: boolean
  isAITutor?: boolean
}

export const SearchResultListSection = ({
  keywords,
  isList = false,
  isConversation = false,
  isAITutor = false,
}: ISearchResultListSection) => {
  const isMobile = useIsMobile(null, 640)
  const { toastSuccess } = useToast()
  const getFullUrl = useReturnFullUrl()
  const { t } = useTranslation()
  const [showSkeleton, setShowSkeleton] = useState(true)
  const [resultList, setResultList] = useState([])
  const router = useAppRouter()
  const { sendClickEvent } = useEventTracking()

  const { useLessonsFlow } = useLessonsStack()
  const { push } = useLessonsFlow()
  const lessonRouter = useAppRouter({ stackPush: push })

  const { selectedCourse, setSelectedCourse, lessonId } = useContext(SelectMaterialContext)
  const lessonSelectLessonId = lessonId ? lessonId : router.query.lessonId

  const { useMaterialsFlow } = useMaterialsStack()
  const { push: materialsPush } = useMaterialsFlow()
  const materialsRouter = useAppRouter({ stackPush: materialsPush })

  const { useAITutorFlow } = useAITutorStack()
  const { push: aiTutorPush } = useAITutorFlow()
  const aiTutorRouter = useAppRouter({ stackPush: aiTutorPush })

  useEffect(() => {
    setShowSkeleton(true)
  }, [keywords])

  const fetchMaterialSearchData = async (value: string) => {
    if (value.length < 2 || !value) return

    const response = await axiosGet(apis.course.search(), {
      param: { keyword: value },
    })

    return response
  }

  const { data: resultData, isFetching } = useQuery({
    queryKey: [keywords],
    queryFn: () => fetchMaterialSearchData(keywords),
  })

  useEffect(() => {
    if (resultData && !isFetching) {
      if (isConversation || isAITutor) {
        setResultList(resultData.courses.filter((item) => !item?.writing_or_speaking))
      } else {
        setResultList(resultData.courses)
      }
      const timeout = setTimeout(() => {
        setShowSkeleton(false)
      }, 300)

      return () => clearTimeout(timeout)
    }
  }, [resultData, isFetching])

  const handleClickSelect = (course) => {
    const isSelected = course.id === selectedCourse?.id

    if (isSelected) {
      setSelectedCourse(undefined)
    } else {
      setSelectedCourse(course)
    }
  }

  const handleClickBookMark = useOnclickHeartPacket({
    packetItems: resultList,
    setPacketItems: setResultList,
    isList: true,
    onAdded: () => toastSuccess(t(n_material.toast_book_mark)),
    onDeleted: () => toastSuccess(t(n_material.toast_delete_book_mark)),
  })

  const SKELETON_ARRAY = isList ? [0, 1, 2, 3, 4, 5, 6, 7] : [0, 1, 2, 3, 4, 5]

  const { useConversationAIFlow } = useConversationAIStack()
  const { push: conversationPush } = useConversationAIFlow()
  const conversationRouter = useAppRouter({ stackPush: conversationPush })

  const onClickCourseDetail = (course) => {
    if (isConversation) {
      conversationRouter.push('', {
        stackActivity: STACK_ACTIVITY_NAME.CONVERSATION_MATERIAL_DETAIL_ACTIVITY,
        stackSendData: {
          materialId: course.id,
        },
      })
      return
    }

    if (isAITutor) {
      aiTutorPush(AI_TUTOR_ACTIVITIES_MAP.AITutorDiscussionMaterialDetailActivity, { materialId: course.id })
      return
    }

    router.asPath.includes('webview/materials')
      ? materialsRouter.push(
          isList
            ? urls.student.newPortal.materials.detail(course.id)
            : urls.student.newPortal.materials.detail(course.id) + `?lessonId=${lessonSelectLessonId}`,
          {
            stackActivity: STACK_ACTIVITY_NAME.MATERIAL_DETAIL_ACTIVITY,
            stackSendData: {
              materialId: course.id,
              lessonId: lessonSelectLessonId,
            },
          }
        )
      : lessonRouter.push(
          isList
            ? urls.student.newPortal.materials.detail(course.id)
            : urls.student.newPortal.materials.detail(course.id) + `?lessonId=${lessonSelectLessonId}`,
          {
            stackActivity: STACK_ACTIVITY_NAME.LESSON_MATERIAL_DETAIL_ACTIVITY,
            stackSendData: {
              materialId: course.id,
              lessonId: lessonSelectLessonId,
            },
          }
        )
  }

  return (
    <div
      className={classNames(
        isList ? 'lg:max-w-[1040px]' : 'lg:max-w-[688px]',
        'h-full w-full border-purple-700 min-h-svh'
      )}
    >
      <div className="Headline2-24Bd">'{t(n_material.search_result(keywords, resultList?.length))}</div>
      <div
        className={classNames(
          isList ? 'md:grid-cols-4 lg:gap-[0px]' : 'md:grid-cols-3 lg:gap-[16px]',
          'mt-[36px] flex flex-col flex-wrap gap-y-[12px] sm:grid sm:grid-cols-2'
        )}
      >
        {showSkeleton
          ? SKELETON_ARRAY.map((item, i) => {
              return (
                <div key={i} className="mr-[16px]">
                  <SelectMaterialCardSkeleton />
                </div>
              )
            })
          : resultList?.map((course) => {
              return (
                <div
                  key={course.id}
                  onClick={() => {
                    sendClickEvent({ button_name: 'material_card', object_id: course.id })
                    onClickCourseDetail(course)
                  }}
                >
                  <SelectMaterialCard
                    horizontal={isMobile}
                    selected={course.id === selectedCourse?.id}
                    onClickSelect={() => handleClickSelect(course)}
                    onClickHeart={(e) => handleClickBookMark(e, course)}
                    title={course.title}
                    subtitle={course.subtitle}
                    is_mine={course.is_mine}
                    category={course.category}
                    image_url={course.image_url}
                    is_revised={course.is_revised}
                    is_today={course.is_today}
                    isList={isList}
                    level={course.level}
                    audioLength={course.mp3_running_time_in_minute}
                  />
                </div>
              )
            })}
      </div>
    </div>
  )
}

const WebviewMaterialSearchSection = ({ isAITutor }) => {
  const { t } = useTranslation()
  const { useLessonsFlow } = useLessonsStack()
  const { push } = useLessonsFlow()

  const { useAITutorFlow } = useAITutorStack()
  const { push: aiTutorPush } = useAITutorFlow()
  const aiTutorRouter = useAppRouter({ stackPush: aiTutorPush })
  const getFullUrl = useReturnFullUrl()

  const onClickSearch = () => {
    if (isAITutor) {
      aiTutorRouter.push(urls.webview.aiTutor.discussion.materialSearch(), {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(urls.webview.aiTutor.discussion.materialSearch()),
        },
      })
    } else {
      push(STACK_ACTIVITY_NAME.LESSON_MATERIAL_SEARCH_LIST_ACTIVITY as LessonsActivityNamesType, {})
    }
  }

  return (
    <div
      onClick={() => onClickSearch()}
      className={'border-1 flex w-full items-center rounded-[8px] border-gray-200 bg-gray-50 p-[12px] cursor-pointer'}
    >
      <div className="ml-0 mr-[8px] self-center">
        <QIcon_search_default color="#80839E" width={24} height={24} />
      </div>

      <div className={'Body2-14Md rounded-[8px] border-none bg-gray-50 p-0 text-gray-500'}>
        {t(n_material_list.all.search_placeholder)}
      </div>
    </div>
  )
}

interface ISelectMaterialBody {
  diffHours?: number
  isList?: boolean
  isConversation?: boolean
  isAITutor?: boolean
}

const fetchLessonInfo = async (lessonId) => {
  const response = await axiosGet(apis.lessons.upcoming(lessonId))

  if (response) return response.data
}

const SelectMaterialBody = ({
  diffHours = 10,
  isList = false,
  isConversation = false,
  isAITutor = false,
}: ISelectMaterialBody) => {
  const isWebview = useIsWebview()
  const { keywords } = useContext(SelectMaterialContext)

  const [categoryId, setCategoryId] = useState(0)
  const [selectedHashId, setSelectedHashId] = useState('All')

  useEffect(() => {
    const handleQueryParamChange = () => {
      const queryParams = new URLSearchParams(window.location.search)
      const category = queryParams.get('category')
      if (category) {
        setCategoryId(Number(category))
      }
    }

    window.addEventListener('popstate', handleQueryParamChange)

    return () => {
      window.removeEventListener('popstate', handleQueryParamChange)
    }
  }, [])

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const category = queryParams.get('category')
    if (category) {
      setCategoryId(Number(category))
    }
  }, [])

  const renderContent = () => {
    if (isList) {
      return (
        <div className="mt-[16px] sm:mt-[40px] w-full">
          {!isWebview && keywords ? (
            <SearchResultListSection keywords={keywords} isList={true} />
          ) : (
            <div className="flex flex-col gap-[24px] sm:gap-[40px]">
              {isWebview && <WebviewMaterialSearchSection isAITutor={isAITutor} />}

              <CategorySection
                categoryId={categoryId}
                setCategoryId={setCategoryId}
                setSelectedHashId={setSelectedHashId}
                isAITutor={isAITutor}
                isList={true}
              />
              <RecommendedListSection setCategoryId={setCategoryId} isList={true} isAITutor={isAITutor} />
            </div>
          )}
        </div>
      )
    }
    if (diffHours <= PREV_TWO_HOUR_FROM_LESSON) {
      return (
        <div className="w-full sm:mt-[36px]">
          <PrevHourMaterialSelect />
        </div>
      )
    }

    if (keywords)
      return (
        <div className="mt-[150px] lg:mt-[36px]">
          <SearchResultListSection keywords={keywords} />
        </div>
      )

    if (categoryId) {
      return (
        <div className="mt-[0px] sm:mt-[220px] lg:mt-[36px]">
          <CategorySection
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            setSelectedHashId={setSelectedHashId}
            isConversation={isConversation}
            isAITutor={isAITutor}
          />
          <CategoryListSection
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            selectedHashId={selectedHashId}
            setSelectedHashId={setSelectedHashId}
            isConversation={isConversation}
            isAITutor={isAITutor}
          />
        </div>
      )
    }

    return (
      <div className={classNames(!isWebview ? 'mt-[0px]' : '', ' sm:mt-[164px] lg:mt-[30px]')}>
        <CategorySection
          categoryId={categoryId}
          setCategoryId={setCategoryId}
          setSelectedHashId={setSelectedHashId}
          isConversation={isConversation}
          isAITutor={isAITutor}
        />
        <RecommendedListSection setCategoryId={setCategoryId} isConversation={isConversation} isAITutor={isAITutor} />
      </div>
    )
  }

  return <div className="relative flex w-full max-w-[1040px] flex-col lg:flex-row lg:gap-[16px]">{renderContent()}</div>
}

export default SelectMaterialBody
